/**
 * 生产环境日志抑制器
 * 确保在生产环境中完全禁用所有日志输出
 */

/**
 * 检查是否为生产环境
 */
function isProductionEnvironment(): boolean {
  // 检查 Vite 环境变量
  if (import.meta.env.PROD === true) return true;
  if (import.meta.env.MODE === 'production') return true;
  
  // 检查域名（部署后）
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    // 如果不是本地开发域名，认为是生产环境
    if (hostname !== 'localhost' && 
        hostname !== '127.0.0.1' && 
        !hostname.includes('localhost') &&
        !hostname.includes('192.168.') &&
        !hostname.includes('10.0.') &&
        hostname !== '') {
      return true;
    }
  }
  
  return false;
}

/**
 * 在生产环境中禁用所有 console 输出
 */
function suppressConsoleInProduction(): void {
  if (!isProductionEnvironment()) return;
  if (typeof window === 'undefined') return;

  // 保存原始方法（仅用于紧急情况）
  const originalMethods = {
    log: console.log,
    info: console.info,
    warn: console.warn,
    error: console.error,
    debug: console.debug,
    trace: console.trace,
    group: console.group,
    groupEnd: console.groupEnd,
    time: console.time,
    timeEnd: console.timeEnd,
    table: console.table,
    count: console.count,
    countReset: console.countReset,
    clear: console.clear,
    dir: console.dir,
    dirxml: console.dirxml,
    assert: console.assert,
  };

  // 创建空函数
  const noop = () => {};

  // 重写所有 console 方法
  console.log = noop;
  console.info = noop;
  console.warn = noop;
  console.debug = noop;
  console.trace = noop;
  console.group = noop;
  console.groupEnd = noop;
  console.time = noop;
  console.timeEnd = noop;
  console.table = noop;
  console.count = noop;
  console.countReset = noop;
  console.clear = noop;
  console.dir = noop;
  console.dirxml = noop;
  console.assert = noop;

  // 保留 console.error 但限制输出（仅用于真正的应用错误）
  console.error = (...args: any[]) => {
    // 只在真正的 Error 对象时输出，且简化信息
    if (args.length > 0 && args[0] instanceof Error) {
      originalMethods.error('App Error:', args[0].message);
    }
  };

  // 添加标记
  (window as any).__CONSOLE_SUPPRESSED__ = true;
  (window as any).__ORIGINAL_CONSOLE__ = originalMethods;

  // 在开发者工具中显示提示
  originalMethods.info('%c🔇 Console output has been suppressed in production', 'color: #888; font-style: italic;');
}

/**
 * 恢复 console 方法（仅用于调试）
 */
function restoreConsole(): void {
  if (typeof window === 'undefined') return;
  
  const originalMethods = (window as any).__ORIGINAL_CONSOLE__;
  if (!originalMethods) return;

  console.log = originalMethods.log;
  console.info = originalMethods.info;
  console.warn = originalMethods.warn;
  console.error = originalMethods.error;
  console.debug = originalMethods.debug;
  console.trace = originalMethods.trace;
  console.group = originalMethods.group;
  console.groupEnd = originalMethods.groupEnd;
  console.time = originalMethods.time;
  console.timeEnd = originalMethods.timeEnd;
  console.table = originalMethods.table;
  console.count = originalMethods.count;
  console.countReset = originalMethods.countReset;
  console.clear = originalMethods.clear;
  console.dir = originalMethods.dir;
  console.dirxml = originalMethods.dirxml;
  console.assert = originalMethods.assert;

  (window as any).__CONSOLE_SUPPRESSED__ = false;
}

// 立即执行抑制
suppressConsoleInProduction();

// 导出函数供调试使用
export {
  isProductionEnvironment,
  suppressConsoleInProduction,
  restoreConsole,
};

// 在开发环境中添加到全局对象
if (!isProductionEnvironment() && typeof window !== 'undefined') {
  (window as any).logSuppressor = {
    isProduction: isProductionEnvironment,
    suppress: suppressConsoleInProduction,
    restore: restoreConsole,
  };
}